<template>
  <div class="af">
    <!-- Header -->
    <div
      v-if="showHeader"
      class="af__header"
    >
      <svg
        class="af__header-icon"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle
          cx="12"
          cy="10"
          r="3"
        />
      </svg>
      <h3 class="af__title">
        {{ $t('checkout.delivery.shippingDetails') }}
      </h3>
    </div>

    <!-- Phone field -->
    <div class="af__group">
      <label
        class="af__label"
        for="alternative-phone-input"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
        {{ $t('profile.alternativePhoneNumber') }}
        <span
          v-if="fieldsRequired"
          class="af__required"
        >*</span>
      </label>
      <ekitabu-masked-phone-input
        :id="'alternative-phone-input'"
        :value="formattedAlternativePhone"
        class="af__phone"
        :label="$t('profile.alternativePhoneNumber')"
        rules="max:9|min:9"
        placeholder="### ### ###"
        type="tel"
        @formattedPhoneChanged="setAlternativeFormattedPhone"
        @countryChanged="onPhoneCountryChanged"
      />
    </div>

    <!-- Address search field -->
    <div class="af__group">
      <label
        class="af__label"
        for="autocomplete"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
          />
          <line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          />
        </svg>
        {{ $t('profile.address') }}
        <span
          v-if="fieldsRequired"
          class="af__required"
        >*</span>
      </label>
      <ekitabu-text-input
        id="autocomplete"
        v-model="searchAddress"
        class="af__text"
        :label="$t('profile.address')"
        :placeholder="$t('profile.address')"
      />
      <p class="af__hint">
        {{ $t('profile.searchAddressText') }}
      </p>
    </div>

    <!-- Address details field -->
    <div class="af__group">
      <label
        class="af__label"
        for="address-info-input"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
        {{ $t('profile.addressDetails') }}
        <span
          v-if="fieldsRequired"
          class="af__required"
        >*</span>
      </label>
      <ekitabu-text-input
        id="address-info-input"
        v-model="line2"
        class="af__text"
        :label="$t('profile.addressDetails')"
        :placeholder="$t('profile.addressDetails')"
      />
    </div>

    <!-- Selected address confirmation -->
    <transition name="af-pop">
      <div
        v-if="hasAddress"
        class="af__confirm"
      >
        <div class="af__confirm-pin">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle
              cx="12"
              cy="10"
              r="3"
            />
          </svg>
        </div>
        <div class="af__confirm-body">
          <span
            v-if="addressComponents.label"
            class="af__confirm-name"
          >{{ addressComponents.label }}</span>
          <span class="af__confirm-line">
            <template v-if="addressComponents.line1">{{ addressComponents.line1 }}</template>
            <template v-if="line2">, {{ line2 }}</template>
          </span>
          <span class="af__confirm-meta">
            <template v-if="addressComponents.city">{{ addressComponents.city }}</template>
            <template v-if="addressComponents.postalCode"> &middot; {{ addressComponents.postalCode }}</template>
            <template v-if="addressComponents.country"> &middot; {{ getCountryText(addressComponents.country) }}</template>
          </span>
        </div>
        <div class="af__confirm-check">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><polyline points="20 6 9 17 4 12" /></svg>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import EkitabuTextInput from '@/components/EkitabuTextInput';
import EkitabuMaskedPhoneInput from '@/components/EkitabuMaskedPhoneInput';
import gql from 'graphql-tag';
import { getCountryText } from '@/utils/CountryCodeHelper';
import { supportedCountries } from '@/utils/Const';
import AddressFragments from '@/graphql/AddressFragments';

const ADDRESS_DETAILS_FRAGMENT = AddressFragments.addressDetailsFragment;

const deliveryCountries = supportedCountries.map(c => c.alpha2.toLowerCase());

const COUNTRY_CONFIG = {
  KE: { capital: 'Nairobi', center: { lat: -1.2920699904133635, lng: 36.821931500512214 } },
  RW: { capital: 'Kigali', center: { lat: -1.9403, lng: 29.8739 } },
  MW: { capital: 'Lilongwe', center: { lat: -13.9626, lng: 33.7741 } },
};

const BOUNDS_DELTA = { lat: 0.5, lng: 0.5 };

export default {
  name: "AddressFields",
  components: {
    EkitabuMaskedPhoneInput,
    EkitabuTextInput,
  },
  props: {
    fieldsRequired: {
      type: Boolean,
      default: false,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      formattedAlternativePhone: "",
      searchAddress: "",
      placeId: null,
      addressComponents: {
        label: null,
        line1: null,
        subLocality: null,
        city: null,
        postalCode: null,
        country: null,
      },
      line2: null,
    };
  },
  apollo: {
    getWebServiceUser: {
      query: gql`
        query getUserInfo {
          getWebServiceUser {
            id
            phoneNumber
            alternativePhoneNumber
          }
        }
      `,
      skip() {
        return !this.$auth?.isAuthenticated;
      },
      result({ data }) {
        this.formattedAlternativePhone = data?.getWebServiceUser?.alternativePhoneNumber;
      },
      error(error) {
        console.warn('getWebServiceUser query failed (guest or unauthorized):', error.message);
      },
    },
    getDefaultAddress: {
      query: gql`
        query getDefaultAddress {
          getDefaultAddress {
            ...AddressDetails
          }
        }
        ${ADDRESS_DETAILS_FRAGMENT}
      `,
      skip() {
        return !this.$auth?.isAuthenticated;
      },
      result({ data }) {
        const address = data?.getDefaultAddress;
        if (!address) return;
        this.placeId = address.placesId;
        this.line2 = address.line2;
        this.addressComponents.line1 = address.line1;
        this.addressComponents.city = address.city;
        this.addressComponents.postalCode = address.postalCode == "" ? null : address.postalCode;
        this.addressComponents.country = address.country;

        const line1 = address.line1;
        const city = address.city;
        this.searchAddress = [line1, city].filter(Boolean).join(', ');
        this.addressComponents.label = line1 || null;
      },
      error(error) {
        console.warn('getDefaultAddress query failed (guest or unauthorized):', error.message);
      },
    },
  },
  computed: {
    isChanged() {
      return this.placeId != this.getDefaultAddress?.placesId ||
        this.line2 != this.getDefaultAddress?.line2 ||
        this.formattedAlternativePhone != this.getWebServiceUser?.alternativePhoneNumber;
    },
    isValid() {
      return this.placeId && this.line2 && this.formattedAlternativePhone;
    },
    mapsAvailable() {
      return typeof google !== 'undefined' && google.maps && google.maps.places;
    },
    hasAddress() {
      return this.placeId && (this.addressComponents.line1 || this.addressComponents.city);
    },
  },
  watch: {
    placeId() {
      this.updateShippingDetails();
    },
    line2() {
      this.updateShippingDetails();
    },
    formattedAlternativePhone() {
      this.updateShippingDetails();
    },
    isChanged() {
      this.$emit('updateIsChanged', this.isChanged);
    },
    isValid() {
      this.$emit('updateIsValid', this.isValid);
    },
  },
  mounted() {
    this.initAutocomplete();
  },
  methods: {
    setAlternativeFormattedPhone(formattedPhone) {
      this.formattedAlternativePhone = formattedPhone;
    },
    onPhoneCountryChanged(country) {
      const iso2 = country?.iso2?.toUpperCase();
      if (iso2 && COUNTRY_CONFIG[iso2]) {
        this.addressComponents.city = COUNTRY_CONFIG[iso2].capital;
      }
    },
    updateShippingDetails() {
      this.$emit('updateShippingDetails', {
        placeId: this.placeId,
        addressDetails: this.line2,
        altPhoneNumber: this.formattedAlternativePhone,
      });
    },
    getCountryText,
    initAutocomplete() {
      if (!this.mapsAvailable) {
        console.warn('Google Maps Places API not available — address autocomplete disabled');
        return;
      }
      try {
        const input = document.getElementById('autocomplete');
        if (!input) return;

        const alpha3 = this.$store.state.locale.countryCode;
        const match = supportedCountries.find(c => c.alpha3 === alpha3);
        const iso2 = match?.alpha2 || 'KE';
        const config = COUNTRY_CONFIG[iso2] || COUNTRY_CONFIG.KE;

        const bounds = {
          north: config.center.lat + BOUNDS_DELTA.lat,
          south: config.center.lat - BOUNDS_DELTA.lat,
          east: config.center.lng + BOUNDS_DELTA.lng,
          west: config.center.lng - BOUNDS_DELTA.lng,
        };

        const options = {
          componentRestrictions: { country: deliveryCountries },
          fields: ["address_components", "place_id", "name"],
          bounds,
          strictBounds: false,
        };
        this._autocomplete = new google.maps.places.Autocomplete(input, options);
        this._autocomplete.addListener("place_changed", () => this.getAddressComponents());
      } catch (e) {
        console.warn('Failed to initialize Google Places autocomplete:', e);
      }
    },
    getAddressComponents() {
      if (!this._autocomplete) return;

      let place;
      try {
        place = this._autocomplete.getPlace();
      } catch (e) {
        console.warn('Failed to get place:', e);
        return;
      }

      if (!place || !place.address_components) return;

      let address1 = "";
      let postcode = "";
      this.placeId = place.place_id;
      let label = place?.name ?? null;

      for (const component of place.address_components) {
        const componentType = component.types[0];

        switch (componentType) {
          case "street_number": {
            address1 = `${component.long_name} ${address1}`;
            break;
          }

          case "route": {
            address1 += component.short_name;
            break;
          }

          case "postal_code": {
            postcode = `${component.long_name}${postcode}`;
            break;
          }

          case "postal_code_suffix": {
            postcode = `${postcode}-${component.long_name}`;
            break;
          }

          case "locality": {
            this.addressComponents.city = component.long_name;
            break;
          }

          case "country": {
            this.addressComponents.country = component.long_name;
            break;
          }
        }
      }

      this.addressComponents.label = label;
      this.addressComponents.line1 = address1 != "" ? address1 : null;
      this.addressComponents.postalCode = postcode != "" ? postcode : null;

      const city = this.addressComponents.city;
      const line1 = this.addressComponents.line1;
      this.searchAddress = [label || line1, city].filter(Boolean).join(', ');
    }
  }
}
</script>

<style scoped lang="scss">
/* ═══════════════════════════════════════════════
   AddressFields — Clean stacked form
   ═══════════════════════════════════════════════ */

.af {
  font-family: $book-font-body;
  margin-top: 16px;
}

/* ── Header ── */
.af__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
}

.af__header-icon {
  color: $book-ink;
  flex-shrink: 0;
}

.af__title {
  font-size: 0.9375rem;
  font-weight: 800;
  color: $book-ink;
  margin: 0;
}

/* ═══ Field groups ═══ */
.af__group {
  position: relative;
  margin-bottom: 16px;
  animation: af-in 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}

/* Phone group must stack above siblings while animation creates stacking contexts */
.af__group:first-child {
  z-index: $z-dropdown;
}

.af__group + .af__group {
  animation-delay: 50ms;
}

.af__group + .af__group + .af__group {
  animation-delay: 100ms;
}

@keyframes af-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ── Labels ── */
.af__label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.6875rem;
  font-weight: 700;
  color: $book-grey;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding-left: 2px;
  transition: color 0.15s ease;
}

.af__label svg {
  color: $book-grey-light;
  flex-shrink: 0;
  transition: color 0.15s ease;
}

.af__required {
  color: $book-red;
  margin-left: 2px;
}

.af__group:focus-within .af__label {
  color: $book-grey-dark;
}

.af__group:focus-within .af__label svg {
  color: $book-ink;
}

/* ── Hide component internal labels ── */
::v-deep .af__text .input-label,
::v-deep .af__phone .input-label {
  display: none !important;
}

/* ═══ Text input styling ═══ */
::v-deep .af__text .input-container {
  max-width: none !important;
}

::v-deep .af__text .input-field {
  border: 1.5px solid $book-grey-lighter !important;
  border-radius: 12px !important;
  background: $book-hover-bg !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

::v-deep .af__text .input-field:focus-within {
  border-color: $book-ink !important;
  box-shadow: 0 0 0 3px rgba($book-ink, 0.05) !important;
  background: white !important;
}

::v-deep .af__text .v-input__slot {
  min-height: 46px !important;
  height: 46px;
  padding: 0 16px !important;
  box-shadow: none !important;
  border-radius: 12px !important;
}

::v-deep .af__text input {
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 500;
  color: $book-ink;
}

::v-deep .af__text input::placeholder {
  color: $book-grey-light !important;
  font-weight: 400 !important;
}

/* ═══ Phone input styling ═══ */
/* .af__phone merges onto the same root element as .input-container */
::v-deep .af__phone.input-container {
  max-width: none !important;
  width: 100% !important;
}

::v-deep .af__phone .input-field {
  border: 1.5px solid $book-grey-lighter !important;
  border-radius: 12px !important;
  background: $book-hover-bg !important;
  height: 46px !important;
  min-height: 46px !important;
  width: 100% !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

::v-deep .af__phone .input-field:focus-within {
  border-color: $book-ink !important;
  box-shadow: 0 0 0 3px rgba($book-ink, 0.05) !important;
  background: white !important;
}

::v-deep .af__phone .vue-tel-input input {
  font-family: $book-font-body;
  font-size: 0.875rem;
  font-weight: 500;
  color: $book-ink;
  height: 100%;
}

::v-deep .af__phone .vti__dropdown {
  border-radius: 12px 0 0 12px;
  height: 100%;
}

/* Country dropdown list */
::v-deep .af__phone .vti__dropdown-list {
  border-radius: 10px;
  border: 1px solid $book-grey-lighter;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  padding: 4px 0;
  min-width: 180px;
  max-width: 220px;
  margin-top: 4px;
}

::v-deep .af__phone .vti__dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 500;
  color: $book-ink;
  transition: background 0.15s ease;

  strong {
    font-weight: 600;
  }

  .vti__flag-wrapper {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &.highlighted {
    background: $book-grey-lightest;
  }
}

/* ── Hint — visible only when address input is focused ── */
.af__hint {
  font-size: 0.6875rem;
  font-weight: 500;
  color: $book-grey-light;
  margin: 5px 0 0;
  padding-left: 2px;
  line-height: 1.35;
  opacity: 0;
  max-height: 0;
  overflow: hidden;
  transition: opacity 0.2s ease, max-height 0.2s ease;
}

.af__group:focus-within .af__hint {
  opacity: 1;
  max-height: 2em;
}

/* ═══ Address confirmation card ═══ */
.af__confirm {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: rgba($book-green, 0.04);
  border: 1px solid rgba($book-green, 0.12);
  border-radius: 12px;
  margin-top: 2px;
}

.af__confirm-pin {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba($book-green, 0.1);
  color: $book-green;
  flex-shrink: 0;
}

.af__confirm-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.af__confirm-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: $book-ink;
  line-height: 1.3;
}

.af__confirm-line {
  font-size: 0.75rem;
  font-weight: 500;
  color: $book-grey-dark;
  line-height: 1.3;
}

.af__confirm-meta {
  font-size: 0.6875rem;
  font-weight: 500;
  color: $book-grey;
  line-height: 1.3;
  margin-top: 1px;
}

.af__confirm-check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: $book-green;
  color: white;
  flex-shrink: 0;
  animation: af-check 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes af-check {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* ── Confirmation transition ── */
.af-pop-enter-active {
  transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.af-pop-leave-active {
  transition: opacity 0.15s ease;
}

.af-pop-enter {
  opacity: 0;
  transform: translateY(-6px);
}

.af-pop-leave-to {
  opacity: 0;
}

/* ═══ Responsive ═══ */
@media (max-width: 599px) {
  .af__group {
    margin-bottom: 14px;
  }

  ::v-deep .af__text input,
  ::v-deep .af__phone .vue-tel-input input {
    font-size: 1rem !important;
  }

  ::v-deep .af__text .v-input__slot {
    min-height: 42px !important;
    height: 42px;
  }

  ::v-deep .af__phone .vue-tel-input {
    min-height: 42px;
  }
}
</style>
